import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Trash2, Plus, Users } from "lucide-react";
import { PaymentCategory, usePaymentCategories } from "@/hooks/payments/usePaymentCategories";
import { CategoryForm } from "./CategoryForm";
import { supabase } from "@/integrations/supabase/client";
import { supportsGroupCategories } from "@/utils/vikingosFeatures";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CategoryManagerProps {
  gymId: string;
  onBack: () => void;
}

export const CategoryManager = ({ gymId, onBack }: CategoryManagerProps) => {
  const { categories, createCategory, updateCategory, deleteCategory } =
    usePaymentCategories(gymId);
  const [editingCategory, setEditingCategory] = useState<PaymentCategory | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<PaymentCategory | null>(null);

  const { data: gymName } = useQuery({
    queryKey: ["gym-name", gymId],
    queryFn: async () => {
      const { data } = await supabase.from("gyms").select("name").eq("id", gymId).maybeSingle();
      return data?.name || null;
    },
    enabled: !!gymId,
  });

  const enableGroupCategories = supportsGroupCategories(gymName);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleCreate = async (data: { name: string; amount: number; validity_days: number; covers_people: number }) => {
    await createCategory.mutateAsync({
      gym_id: gymId,
      ...data,
    });
    setIsCreating(false);
  };

  const handleUpdate = async (data: { name: string; amount: number; validity_days: number; covers_people: number }) => {
    if (!editingCategory) return;
    await updateCategory.mutateAsync({
      id: editingCategory.id,
      data,
    });
    setEditingCategory(null);
  };

  const handleDelete = async () => {
    if (!deletingCategory) return;
    await deleteCategory.mutateAsync(deletingCategory.id);
    setDeletingCategory(null);
  };

  if (isCreating) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsCreating(false)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium">Nueva categoría</h3>
        </div>
        <CategoryForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreating(false)}
          isLoading={createCategory.isPending}
          enableGroupCategories={enableGroupCategories}
        />
      </div>
    );
  }

  if (editingCategory) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setEditingCategory(null)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium">Editar categoría</h3>
        </div>
        <CategoryForm
          category={editingCategory}
          onSubmit={handleUpdate}
          onCancel={() => setEditingCategory(null)}
          isLoading={updateCategory.isPending}
          enableGroupCategories={enableGroupCategories}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="font-medium">Gestionar categorías</h3>
      </div>

      <p className="text-sm text-muted-foreground">
        Categorías existentes:
      </p>

      {categories.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No hay categorías configuradas
        </p>
      ) : (
        <div className="space-y-2">
          {categories.map((category) => (
            <div
              key={category.id}
              className="border rounded-md p-3 space-y-1"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium flex items-center gap-2">
                  {category.name}
                  {enableGroupCategories && category.covers_people > 1 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5">
                      <Users className="h-3 w-3" />
                      {category.covers_people}x1
                    </span>
                  )}
                </span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEditingCategory(category)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setDeletingCategory(category)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {formatCurrency(category.amount)} · {category.validity_days} días
                {enableGroupCategories && category.covers_people > 1 && (
                  <> · cubre {category.covers_people} personas</>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => setIsCreating(true)}
      >
        <Plus className="h-4 w-4 mr-2" />
        Agregar categoría
      </Button>

      <AlertDialog
        open={!!deletingCategory}
        onOpenChange={(open) => !open && setDeletingCategory(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La categoría "
              {deletingCategory?.name}" será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
